import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { getDealMembersForAssignment, updateChecklistItem, deleteChecklistItem } from '../../api/checklist-queries';
import { getDocumentsByDeal } from '../../api/queries_v2';
import { useNotifications } from '../../notifications';
import LoadingSpinner from '../loading-spinner';
import { Checkbox } from '../checkbox-or-radio-field';
import DeleteItemModal from './DeleteItemModal';
import styles from './AddItemForm.module.scss';

const selectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    fontSize: '14px',
    borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(33, 150, 243, 0.1)' : null,
    '&:hover': {
      borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    },
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: '200px',
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontSize: '14px',
    color: '#999',
  }),
};

interface ChecklistItem {
  id: string;
  content: string;
  dueDate?: string | null;
  reminderDaysBefore?: number | null;
  autoCompleteOnSignature?: boolean | null;
  assignedTo?: {
    id: string;
    fullName: string;
  } | null;
  document?: {
    id: string;
    title: string;
  } | null;
}

interface EditItemFormProps {
  dealId: string;
  item: ChecklistItem;
  onSuccess: () => void;
  onCancel: () => void;
  refetch: () => Promise<any>;
}

export const EditItemForm: React.FC<EditItemFormProps> = ({ dealId, item, onSuccess, onCancel, refetch }) => {
  const { formatMessage } = useIntl();
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [content, setContent] = useState(item.content || '');
  const [dueDate, setDueDate] = useState(item.dueDate || '');
  const [assignedToId, setAssignedToId] = useState(item.assignedTo?.id || '');
  const [documentId, setDocumentId] = useState(item.document?.id || '');
  const [autoCompleteOnSignature, setAutoCompleteOnSignature] = useState(item.autoCompleteOnSignature || false);
  const [sendReminder, setSendReminder] = useState(!!item.reminderDaysBefore);
  const [reminderDaysBefore, setReminderDaysBefore] = useState(item.reminderDaysBefore || 1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [updateItem, { loading: updating }] = useMutation(updateChecklistItem);
  const [deleteItem, { loading: deleting }] = useMutation(deleteChecklistItem);

  // Fetch deal members for assignment
  const { data: membersData, loading: membersLoading } = useQuery(getDealMembersForAssignment, {
    variables: { dealId },
    skip: !dealId,
    fetchPolicy: 'cache-and-network',
  });

  // Fetch deal documents
  const { data: documentsData, loading: documentsLoading } = useQuery(getDocumentsByDeal, {
    variables: { dealId },
    skip: !dealId,
  });

  const members = membersData?.dealMembersForAssignment || [];
  const documents = documentsData?.getDocumentsByDeal || [];

  // Filter out signed documents
  const unsignedDocuments = documents.filter((doc: any) =>
    !doc.signedDocumentUrl && !doc.isExecuted
  );

  // Build sorted options for react-select
  const memberOptions = useMemo(() =>
    [...members]
      .sort((a: any, b: any) => (a.fullName || '').localeCompare(b.fullName || ''))
      .map((m: any) => ({ value: m.id, label: m.fullName })),
    [members]
  );

  const documentOptions = useMemo(() =>
    [...unsignedDocuments]
      .sort((a: any, b: any) => (a.title || '').localeCompare(b.title || ''))
      .map((d: any) => ({ value: d.id, label: d.title })),
    [unsignedDocuments]
  );

  const isSubmitting = updating || deleting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim()) {
      notifyError({
        message: {
          id: 'checklist.item.content.required',
          defaultMessage: 'Please enter item content',
        },
      });
      return;
    }

    try {
      const result = await updateItem({
        variables: {
          id: item.id,
          content: content.trim(),
          dueDate: dueDate || null,
          assignedToId: assignedToId || null,
          documentId: documentId || null,
          autoCompleteOnSignature: autoCompleteOnSignature || null,
          reminderDaysBefore: (sendReminder && dueDate) ? reminderDaysBefore : null,
        },
      });

      if (result.data?.updateChecklistItem?.errors?.length) {
        notifyError({
          message: {
            id: 'checklist.item.update.error',
            defaultMessage: result.data.updateChecklistItem.errors[0] || 'Failed to update item',
          },
        });
        return;
      }

      notifySuccess({
        message: {
          id: 'checklist.item.update.success',
          defaultMessage: 'Item updated successfully',
        },
      });

      await refetch();
      onSuccess();
    } catch (error) {
      notifyError({
        message: {
          id: 'checklist.item.update.error',
          defaultMessage: 'Failed to update item',
        },
      });
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deleteItem({
        variables: { id: item.id },
      });

      if (result.data?.deleteChecklistItem?.errors?.length) {
        notifyError({
          message: {
            id: 'checklist.item.delete.error',
            defaultMessage: result.data.deleteChecklistItem.errors[0] || 'Failed to delete item',
          },
        });
        return;
      }

      notifySuccess({
        message: {
          id: 'checklist.item.delete.success',
          defaultMessage: 'Item deleted successfully',
        },
      });

      await refetch();
      onSuccess();
    } catch (error) {
      notifyError({
        message: {
          id: 'checklist.item.delete.error',
          defaultMessage: 'Failed to delete item',
        },
      });
    }
  };

  if (membersLoading || documentsLoading) {
    return (
      <div className={styles.addItemForm__loading}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={styles.addItemForm}>
      <Form onSubmit={handleSubmit} className={styles.addItemForm__form}>
        {/* Item Content */}
        <Form.Group className={styles.addItemForm__field}>
          <Form.Label>
            <FormattedMessage id="checklist.item.content" defaultMessage="Item" />
            <span className={styles.addItemForm__required}>*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder={formatMessage({
              id: 'checklist.item.content.placeholder',
              defaultMessage: 'e.g., Review property inspection report',
            })}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            className={styles.addItemForm__textarea}
            disabled={isSubmitting}
          />
        </Form.Group>

        {/* Linked Document */}
        <Form.Group className={styles.addItemForm__field}>
          <Form.Label>
            <FormattedMessage id="checklist.item.linkedDocument" defaultMessage="Linked Document (Optional)" />
          </Form.Label>
          <Select
            options={documentOptions}
            value={documentOptions.find((o: any) => o.value === documentId) || null}
            onChange={(option: any) => setDocumentId(option ? option.value : '')}
            isClearable
            isDisabled={isSubmitting}
            placeholder={formatMessage({ id: 'checklist.item.linkedDocument.none', defaultMessage: 'No document linked' })}
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuPlacement="auto"
          />
        </Form.Group>

        {/* Auto-complete on Signature */}
        {documentId && (
          <Form.Group className={styles.addItemForm__checkboxField}>
            <Checkbox
              id="auto-complete"
              name="auto-complete"
              label={formatMessage({
                id: 'checklist.item.autoComplete',
                defaultMessage: 'Automatically complete when signed',
              })}
              checked={autoCompleteOnSignature}
              onChange={(e) => setAutoCompleteOnSignature(e.target.checked)}
              disabled={isSubmitting}
            />
          </Form.Group>
        )}

        {/* Assigned To + Due Date side by side */}
        <div className={styles.addItemForm__dueDateRow}>
          <Form.Group className={styles.addItemForm__dueDateField}>
            <Form.Label>
              <FormattedMessage id="checklist.item.assignedTo" defaultMessage="Assigned To (Optional)" />
            </Form.Label>
            <Select
              options={memberOptions}
              value={memberOptions.find((o: any) => o.value === assignedToId) || null}
              onChange={(option: any) => setAssignedToId(option ? option.value : '')}
              isClearable
              isDisabled={isSubmitting}
              placeholder={formatMessage({ id: 'checklist.item.assignedTo.none', defaultMessage: 'No one assigned' })}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
            />
          </Form.Group>

          <Form.Group className={styles.addItemForm__dueDateField}>
            <Form.Label>
              <FormattedMessage id="checklist.item.dueDate" defaultMessage="Due Date (Optional)" />
            </Form.Label>
            <Form.Control
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              disabled={isSubmitting}
            />
          </Form.Group>
        </div>

        {/* Send Reminder */}
        {dueDate && (
          <Form.Group className={styles.addItemForm__checkboxField}>
            <Checkbox
              id="send-reminder"
              name="send-reminder"
              label={formatMessage({
                id: 'checklist.item.sendReminder',
                defaultMessage: 'Send reminder before due date',
              })}
              checked={sendReminder}
              onChange={(e) => setSendReminder(e.target.checked)}
              disabled={isSubmitting}
            />
            {sendReminder && (
              <div className={styles.addItemForm__reminderDays}>
                <Form.Label>
                  <FormattedMessage id="checklist.item.reminderDaysBefore" defaultMessage="Days before:" />
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="30"
                  value={reminderDaysBefore}
                  onChange={(e) => setReminderDaysBefore(parseInt(e.target.value, 10))}
                  className={styles.addItemForm__reminderInput}
                  disabled={isSubmitting}
                />
              </div>
            )}
          </Form.Group>
        )}

        {/* Form Actions */}
        <div className={styles.addItemForm__actions}>
          <Button
            variant="outline-danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={isSubmitting}
            className={styles.addItemForm__deleteButton}
          >
            <FormattedMessage id="cta.delete" defaultMessage="Delete" />
          </Button>
          <div className={styles.addItemForm__rightActions}>
            <Button
              variant="link"
              onClick={onCancel}
              disabled={isSubmitting}
              className={styles.addItemForm__cancelButton}
            >
              <FormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting || !content.trim()}
            >
              {updating ? (
                <FormattedMessage id="cta.saving" defaultMessage="Saving..." />
              ) : (
                <FormattedMessage id="cta.save" defaultMessage="Save" />
              )}
            </Button>
          </div>
        </div>
      </Form>

      <DeleteItemModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        itemContent={item.content}
        onConfirm={handleDelete}
        isDeleting={deleting}
      />
    </div>
  );
};

export default EditItemForm;
